import { Badge, Box, Button, HStack, Icon, Spinner, Text, VStack } from '@elkaso-app/web-design';
import { CmsCatalogCategoryDto, useGetCmsCatalogCategoriesApi } from 'apis/cms-catalog-categories';
import { FiAlertCircle, FiChevronRight } from 'react-icons/fi';
import { isSelectableDepth } from './editable-deals-grid/category-tree-helpers';

interface CascadeCategoryColumnProps {
  parentId?: number;
  selectedId?: number;
  onSelect: (item: CmsCatalogCategoryDto) => void;
  /** Column index — also the tree depth of items rendered in this column (0 = roots, max 4). */
  columnIndex: number;
}

export const CascadeCategoryColumn = ({ parentId, selectedId, onSelect, columnIndex }: CascadeCategoryColumnProps) => {
  const isRoots = parentId === undefined;
  const depth = columnIndex;
  const selectableColumn = isSelectableDepth(depth);
  const { data, isLoading, isError, refetch } = useGetCmsCatalogCategoriesApi(isRoots ? {} : { parentId });

  const items = data?.data ?? [];

  return (
    <Box
      width='240px'
      flexShrink={0}
      borderRight='1px solid'
      borderColor='gray.200'
      height='100%'
      display='flex'
      flexDirection='column'
      role='listbox'
      aria-label={`Depth ${depth}${selectableColumn ? ' — select to save' : ''}`}
      tabIndex={columnIndex === 0 ? 0 : -1}
      data-cascade-column-index={columnIndex}>
      <HStack
        justify='space-between'
        px='sm'
        py='xs'
        bg={selectableColumn ? 'red.50' : 'gray.50'}
        borderBottom='1px solid'
        borderColor='gray.200'
        flexShrink={0}>
        <Text fontSize='xs' fontWeight='semibold' color='gray.600' textTransform='uppercase' letterSpacing='0.04em'>
          Depth {depth}
          {depth === 0 && ' · Roots'}
        </Text>
        {selectableColumn && (
          <Badge colorScheme='red' variant='subtle' fontSize='10px'>
            Selectable
          </Badge>
        )}
      </HStack>

      <Box overflowY='auto' flex='1'>
        {isLoading && (
          <HStack justify='center' py='lg'>
            <Spinner size='sm' color='red.500' />
            <Text fontSize='sm' color='gray.500'>
              Loading...
            </Text>
          </HStack>
        )}

        {isError && (
          <VStack spacing='sm' p='md' align='stretch'>
            <HStack color='red.500' spacing='xs'>
              <Icon as={FiAlertCircle} />
              <Text fontSize='sm'>Couldn&apos;t load</Text>
            </HStack>
            <Button size='xs' variant='outline' onClick={() => refetch()}>
              Retry
            </Button>
          </VStack>
        )}

        {!isLoading && !isError && items.length === 0 && (
          <Text fontSize='sm' color='gray.500' p='md'>
            No subcategories.
          </Text>
        )}

        {!isLoading && !isError && items.length > 0 && (
          <VStack spacing={0} align='stretch'>
            {items.map((item) => {
              const isSelected = selectedId === item.id;
              const isUntitled = item.title === null;
              const canSelectHere = isSelectableDepth(item.depth);
              return (
                <HStack
                  key={item.id}
                  as='button'
                  type='button'
                  onClick={() => onSelect(item)}
                  spacing={0}
                  justify='space-between'
                  px='sm'
                  py='xs'
                  bg={isSelected ? 'red.50' : 'white'}
                  color={isSelected ? 'red.700' : 'gray.800'}
                  _hover={{ bg: isSelected ? 'red.50' : 'gray.50' }}
                  _focus={{ outline: '2px solid', outlineColor: 'red.300', outlineOffset: '-2px' }}
                  role='option'
                  aria-selected={isSelected}
                  cursor='pointer'>
                  <Text
                    fontSize='sm'
                    textAlign='left'
                    fontWeight={isSelected ? 'semibold' : 'normal'}
                    fontStyle={isUntitled ? 'italic' : 'normal'}
                    color={isUntitled ? 'gray.500' : undefined}
                    noOfLines={2}>
                    {item.title ?? `Untitled (id: ${item.id})`}
                  </Text>
                  <Icon as={FiChevronRight} boxSize='14px' color={canSelectHere ? 'red.400' : 'gray.400'} />
                </HStack>
              );
            })}
          </VStack>
        )}
      </Box>
    </Box>
  );
};
